import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Wallet, Save, ShoppingBag, Copy, RefreshCw, ExternalLink, MessageCircle, Percent, AlertTriangle } from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();
  const [loyalty, setLoyalty] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [wabis, setWabis] = useState(null);
  const [tax, setTax] = useState(null);
  const [busy, setBusy] = useState(false);
  const [merchantBusy, setMerchantBusy] = useState(false);
  const [wabisBusy, setWabisBusy] = useState(false);
  const [taxBusy, setTaxBusy] = useState(false);
  const [wabisTokenDraft, setWabisTokenDraft] = useState("");
  const [wabisTesting, setWabisTesting] = useState(false);
  const [wabisTestRecipient, setWabisTestRecipient] = useState("");

  useEffect(() => {
    api.get("/settings/loyalty").then(r => setLoyalty(r.data));
    api.get("/settings/merchant").then(r => setMerchant(r.data));
    api.get("/settings/wabis").then(r => setWabis(r.data)).catch(() => {});
    api.get("/settings/tax").then(r => setTax(r.data)).catch(() => {});
  }, []);

  const saveTax = async () => {
    setTaxBusy(true);
    try {
      const { data } = await api.patch("/settings/tax", {
        enabled: !!tax.enabled,
        default_gst_percent: parseFloat(tax.default_gst_percent) || 0,
      });
      setTax(data);
      toast.success(`Tax collection ${data.enabled ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTaxBusy(false); }
  };

  const saveLoyalty = async () => {
    setBusy(true);
    try {
      await api.patch("/settings/loyalty", {
        enabled: !!loyalty.enabled,
        earn_percent: parseFloat(loyalty.earn_percent) || 0,
        redeem_cap_percent: parseFloat(loyalty.redeem_cap_percent) || 0,
        min_invoice_to_earn: parseFloat(loyalty.min_invoice_to_earn) || 0,
      });
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const saveMerchant = async () => {
    setMerchantBusy(true);
    try {
      const { data } = await api.patch("/settings/merchant", {
        currency: merchant.currency,
        store_name: merchant.store_name,
        storefront_base_url: (merchant.storefront_base_url || "").trim().replace(/\/+$/, ""),
        google_product_category: merchant.google_product_category,
      });
      setMerchant(data);
      toast.success("Merchant settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setMerchantBusy(false); }
  };

  const regenerateToken = async () => {
    if (!window.confirm("Regenerate feed token? Any existing Google Merchant Centre scheduled-fetch URLs will break and must be updated.")) return;
    try {
      const { data } = await api.post("/settings/merchant/regenerate-token");
      setMerchant(prev => ({ ...prev, feed_token: data.feed_token }));
      toast.success("Token regenerated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const triggerCron = async () => {
    try {
      const { data } = await api.post("/cron/run-vaccination-reminders");
      toast.success(`Reminder job sent ${data.sent} messages`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveWabis = async () => {
    setWabisBusy(true);
    try {
      const payload = {
        enabled: !!wabis.enabled,
        api_url: (wabis.api_url || "").trim(),
        phone_number_id: (wabis.phone_number_id || "").trim(),
        business_name: (wabis.business_name || "").trim(),
        sender_display_name: (wabis.sender_display_name || "").trim(),
      };
      if (wabisTokenDraft.trim()) payload.access_token = wabisTokenDraft.trim();
      const { data } = await api.patch("/settings/wabis", payload);
      setWabis(data);
      setWabisTokenDraft("");
      toast.success("Wabis settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setWabisBusy(false); }
  };

  const clearWabisToken = async () => {
    if (!window.confirm("Clear the saved Wabis access token? You'll need to paste it again to send messages.")) return;
    try {
      await api.post("/settings/wabis/clear-token");
      const { data } = await api.get("/settings/wabis");
      setWabis(data);
      setWabisTokenDraft("");
      toast.success("Access token cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const testWabis = async () => {
    setWabisTesting(true);
    try {
      const params = wabisTestRecipient.trim() ? `?test_recipient=${encodeURIComponent(wabisTestRecipient.trim())}` : "";
      const { data } = await api.post(`/settings/wabis/test${params}`);
      if (data.ok && data.live) toast.success(data.message);
      else if (data.live) toast.error(data.message);
      else toast.info(data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setWabisTesting(false); }
  };

  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const storefront = (merchant?.storefront_base_url || "").replace(/\/+$/, "");

  if (!loyalty || !merchant) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Configuration</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Settings</h1>
      </div>

      {/* Tax Management */}
      <Card className="p-6" data-testid="tax-card">
        <div className="flex items-center gap-3">
          <Percent className="w-5 h-5 text-primary" />
          <div className="flex-1">
            <div className="font-heading text-xl font-semibold">Tax Management</div>
            <div className="text-sm text-muted-foreground">Global GST collection toggle. When OFF, every new bill is created with <strong>zero tax</strong> — line-level GST is ignored across POS, services, grooming, and credit invoices.</div>
          </div>
          {tax && (
            <Switch checked={!!tax.enabled} onCheckedChange={(v) => setTax(p => ({ ...p, enabled: v }))} data-testid="tax-toggle" />
          )}
        </div>
        {tax ? (
          <>
            {!tax.enabled && (
              <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Tax is OFF — new invoices will show GST as ₹0. Existing invoices keep their original tax. Re-enable any time.</span>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              <div>
                <Label>Default GST % (applied to new products/services when tax is ON)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={tax.default_gst_percent ?? 0}
                  onChange={e => setTax(p => ({ ...p, default_gst_percent: e.target.value }))}
                  disabled={!tax.enabled}
                  data-testid="tax-default-percent"
                />
                <div className="text-xs text-muted-foreground mt-1">Reference rate only — used as a default placeholder in forms. Each item still has its own GST % field.</div>
              </div>
            </div>
            <Button className="mt-5" onClick={saveTax} disabled={taxBusy} data-testid="save-tax"><Save className="w-4 h-4 mr-1" />{taxBusy ? "Saving…" : "Save tax settings"}</Button>
          </>
        ) : (
          <div className="text-sm text-muted-foreground mt-4">Loading…</div>
        )}
      </Card>

      {/* Loyalty */}
      <Card className="p-6">
        <div className="flex items-center gap-3">
          <Wallet className="w-5 h-5 text-primary" />
          <div className="flex-1">
            <div className="font-heading text-xl font-semibold">Loyalty wallet</div>
            <div className="text-sm text-muted-foreground">Customers earn wallet credit on each invoice; redeem at POS.</div>
          </div>
          <Switch checked={!!loyalty.enabled} onCheckedChange={(v) => setLoyalty(p => ({ ...p, enabled: v }))} data-testid="loyalty-toggle" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          <div>
            <Label>Earn % of grand total</Label>
            <Input type="number" step="0.1" value={loyalty.earn_percent} onChange={e => setLoyalty(p => ({ ...p, earn_percent: e.target.value }))} data-testid="earn-percent" />
            <div className="text-xs text-muted-foreground mt-1">e.g. 1.0 = ₹1 per ₹100</div>
          </div>
          <div>
            <Label>Redeem cap % of bill</Label>
            <Input type="number" step="1" value={loyalty.redeem_cap_percent} onChange={e => setLoyalty(p => ({ ...p, redeem_cap_percent: e.target.value }))} data-testid="redeem-cap" />
            <div className="text-xs text-muted-foreground mt-1">e.g. 50 = up to half the bill</div>
          </div>
          <div>
            <Label>Min invoice to earn (₹)</Label>
            <Input type="number" step="1" value={loyalty.min_invoice_to_earn} onChange={e => setLoyalty(p => ({ ...p, min_invoice_to_earn: e.target.value }))} data-testid="min-earn" />
          </div>
        </div>
        <Button className="mt-5" onClick={saveLoyalty} disabled={busy} data-testid="save-loyalty"><Save className="w-4 h-4 mr-1" />Save</Button>
      </Card>

      {/* Google Merchant Centre */}
      <Card className="p-6" data-testid="merchant-card">
        <div className="flex items-center gap-3">
          <ShoppingBag className="w-5 h-5 text-primary" />
          <div className="flex-1">
            <div className="font-heading text-xl font-semibold">Google Merchant Centre</div>
            <div className="text-sm text-muted-foreground">Connect your products to Google Shopping via a scheduled XML feed.</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          <div>
            <Label>Currency code</Label>
            <Input value={merchant.currency || ""} onChange={e => setMerchant(p => ({ ...p, currency: e.target.value.toUpperCase() }))} placeholder="INR" data-testid="merchant-currency" />
          </div>
          <div className="sm:col-span-2">
            <Label>Store name (feed title)</Label>
            <Input value={merchant.store_name || ""} onChange={e => setMerchant(p => ({ ...p, store_name: e.target.value }))} data-testid="merchant-store" />
          </div>
          <div className="sm:col-span-3">
            <Label>Storefront base URL (where shoppers will land) <span className="text-rose-600">*</span></Label>
            <Input
              value={merchant.storefront_base_url || ""}
              onChange={e => setMerchant(p => ({ ...p, storefront_base_url: e.target.value }))}
              placeholder="https://store.furmigo.shop"
              data-testid="merchant-storefront"
            />
            <div className="text-xs text-muted-foreground mt-1">
              Used as the prefix for invoice public links and Google Merchant feed (<code className="bg-muted px-1 rounded">/shop/&lt;product_id&gt;</code>, <code className="bg-muted px-1 rounded">/inv/&lt;id&gt;</code>).
              Must be your <strong>original public domain</strong> (e.g. <code className="bg-muted px-1 rounded">https://store.furmigo.shop</code>) — never a preview / staging URL.
            </div>
            {!merchant.storefront_base_url && (
              <div className="mt-2 text-xs bg-rose-50 border border-rose-200 rounded p-2 text-rose-700">
                ⚠ Required. Until set, Google Merchant feed will return an error and invoice links will use a relative path.
              </div>
            )}
          </div>
          <div className="sm:col-span-3">
            <Label>Google product category (optional)</Label>
            <Input value={merchant.google_product_category || ""} onChange={e => setMerchant(p => ({ ...p, google_product_category: e.target.value }))} data-testid="merchant-gpc" />
          </div>
        </div>
        <Button className="mt-4" onClick={saveMerchant} disabled={merchantBusy} data-testid="save-merchant"><Save className="w-4 h-4 mr-1" />Save merchant settings</Button>

        {/* Feed URLs */}
        <div className="mt-6 border-t border-border pt-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Scheduled fetch URLs (one per branch)</div>
          <FeedUrls feedToken={merchant.feed_token} backendOrigin={backendOrigin} storefront={storefront} />
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={regenerateToken} data-testid="regen-token"><RefreshCw className="w-4 h-4 mr-1" />Regenerate token</Button>
            <Button variant="outline" asChild>
              <a href="https://merchants.google.com/" target="_blank" rel="noreferrer" data-testid="open-mc">
                <ExternalLink className="w-4 h-4 mr-1" />Open Merchant Centre
              </a>
            </Button>
          </div>
          <div className="mt-4 text-xs text-muted-foreground space-y-1">
            <div className="font-semibold uppercase tracking-widest text-foreground">Setup steps</div>
            <ol className="list-decimal pl-5 space-y-0.5">
              <li>In Merchant Centre, go to <em>Products → Feeds → Add primary feed</em>.</li>
              <li>Pick country and language; choose <em>Scheduled fetch</em>.</li>
              <li>Paste the branch feed URL above. Set fetch frequency (daily recommended).</li>
              <li>Submit your storefront URL above for Merchant Centre <em>website verification</em>.</li>
            </ol>
          </div>
        </div>
      </Card>

      {/* WhatsApp consolidated section → dedicated page */}
      <Card className="p-6" data-testid="whatsapp-manage-link">
        <div className="flex items-center gap-3 flex-wrap">
          <MessageCircle className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-[200px]">
            <div className="font-heading text-xl font-semibold">Manage WhatsApp</div>
            <div className="text-sm text-muted-foreground">Integration credentials, message templates (with images + links), and message logs — all in one place.</div>
          </div>
          {wabis && (
            <div className={`text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${wabis.mode === "live" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="wabis-mode-badge">
              {wabis.mode === "live" ? "Live" : "Mock"}
            </div>
          )}
          <Button onClick={() => navigate("/settings/whatsapp")} data-testid="open-whatsapp-manage">
            Open
          </Button>
        </div>
      </Card>

      {/* Vaccination cron */}
      <Card className="p-6">
        <div className="font-heading text-xl font-semibold">Vaccination reminders</div>
        <div className="text-sm text-muted-foreground mt-1">
          A scheduled job runs daily at <span className="font-mono">09:00 UTC</span> and sends WhatsApp reminders for pets whose next vaccination is due within 3 days.
        </div>
        <Button variant="outline" onClick={triggerCron} className="mt-4" data-testid="trigger-cron">Run reminder job now</Button>
      </Card>
    </div>
  );
}

function FeedUrls({ feedToken, backendOrigin, storefront }) {
  const [branches, setBranches] = useState([]);

  useEffect(() => { api.get("/branches").then(r => setBranches(r.data)); }, []);

  if (!feedToken) return <div className="text-sm text-muted-foreground">Feed token unavailable.</div>;

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <div className="space-y-2">
      {branches.map(b => {
        const url = `${backendOrigin}/api/feeds/google-merchant.xml?branch=${b.id}&key=${feedToken}`;
        return (
          <div key={b.id} className="rounded-md border border-border p-3" data-testid={`feed-url-${b.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-medium text-sm">{b.name}</div>
                <div className="text-xs text-muted-foreground">Storefront base: <span className="font-mono">{storefront}</span></div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => copy(url)} data-testid={`copy-feed-${b.id}`}><Copy className="w-3 h-3 mr-1" />Copy</Button>
                <Button size="sm" variant="outline" asChild>
                  <a href={url} target="_blank" rel="noreferrer" data-testid={`preview-feed-${b.id}`}><ExternalLink className="w-3 h-3 mr-1" />Preview</a>
                </Button>
              </div>
            </div>
            <input readOnly value={url} className="mt-2 w-full text-xs font-mono bg-muted rounded px-2 py-1.5 border border-border" onFocus={e => e.target.select()} />
          </div>
        );
      })}
      {branches.length === 0 && <div className="text-sm text-muted-foreground">No branches.</div>}
    </div>
  );
}
